/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2016, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'jquery',
	'underscore',
	'../../nls/StringResources',
	'text!../templates/TopBottomDialog.template',
	'doT',
	'../../lib/@waca/core-client/js/core-client/ui/properties/CheckBox',
	'jquery-ui'
], function(View, $, _, stringResources, TopBottomDialogTemplate, dot, CheckBox) {

	var TopBottomView = null;
	var MIN_NUMBER = 1,
		MAX_NUMBER = 100,
		DEFAULT_NUMBER = 10;
		/**
		 * @classdesc  Creates a topBottom dialog
		 *
		 * @public
		 * @callback callback
		 * @param {object} options.eventHandler - Event handler
		 * @param {object} [options.state] - If you want to pass the existing top/bottom state
		 * @param {Array} [options.contextColumns] - An array of columns to search from for attribute column, if omitted this field is not shown
		 * @param {boolean} [options.allowRankColumn] - Puts a checkbox on the dialog to add rank column on creation.
		 * @param {callback} [options.onSearch] - on search call back
		 */
	TopBottomView = View.extend({

		init: function(options) {
			// Setup dimensions for OverlayView base class
			_.extend(this, options);

			TopBottomView.inherited('init', this, arguments);
			this._state = options.state.getTopBottomState ? options.state.getTopBottomState() : null;
			this._selectionHandler = options.actions.apply;
			this._contextColumns = options.state.contextColumns;
			this._allowPercentOperations = options.state.allowPercentOperations;
			this.allowRankColumn = options.state.allowRankColumn;
			this._onSearch = options.state.onSearch;

			this._iHeight = '212';
			this._iWidth = '290';
		},

		/*
			 * @return `true` iff there is a Top-N operation configured
			 */
		_isTopNOn: function() {
			return !!this._state;
		},

		/**
			 * @return a bare-bones Top-N state, even if no Top-N operation is presently configured
			 */
		_ensureState: function() {
			if (!this._state) {
				this._state = {
					value: DEFAULT_NUMBER
				};
			}
			return this._state;
		},

		/*
			 * Responds to a change in the Top-N state represented by this dialog
			 */
		_onStateChange: function() {
			this.$clearButton.toggleClass('disabled', !this._canClearState());
			if (this.allowRankColumn) {
				this._updateRankUI();
			}
			if (this._verifyState()) {
				this._selectionHandler(this._state);
			}
			this.$selectionLabel.textContent = this._getSelectionLabel();
		},

		/*
		 * Changes selection label depending on current state
		 */
		_getSelectionLabel: function() {
			return !this._getType() || this._actionTypeSupportRank() ? stringResources.get('topbottomNumberOfResultsLabel') : stringResources.get('topbottomPercentLabel');
		},

		/*
			 * @return `true` iff the clear button should be enabled
			 */
		_canClearState: function() {
			return !!(this._state && this._verifyState());
		},

		/*
			 * Clears the Top-N state represented by this dialog
			 */
		_clear: function() {
			var oldState = this._state;
			this._state = null;
			if (oldState) {
				this._clearUI();
				this._onStateChange();
			}
		},

		/*
			 * @return the type (e.g. top count, bottom percent) of the Top-N state represented by this dialog
			 */
		_getType: function() {
			return this._state && this._state.type;
		},

		/*
			 * Sets the type
			 * @param {string} type
			 */
		_setType: function(type) {
			var oldType = this._getType();
			if (oldType !== type) {
				this._ensureState().type = type;
				this._onStateChange();
			}
		},

		/*
			 * @return the value (i.e. number) of the Top-N state represented by this dialog
			 */
		_getValue: function() {
			return (this._state && this._state.value) || DEFAULT_NUMBER;
		},

		/*
			 * Sets the value
			 * @param {int} value
			 */
		_setValue: function(value) {
			var oldValue = this._getValue();
			if (oldValue !== value) {
				this._ensureState().value = value;
				this._onStateChange();
			}
		},

		/*
			 * @return the context (i.e. column id) of the Top-N state represented by this dialog
			 */
		_getContext: function() {
			return this._state && this._state.context && this._state.context.itemId;
		},

		/*
			 * @return the context label (i.e. label of the column) of the Top-N state represented by this dialog
			 */
		_getContextLabel: function() {
			if (this._state && this._state.context) {
				var column = _.find(this._contextColumns, function(column) {
					return column.getId() === this._state.context.itemId;
				}.bind(this));
				return column && column.getLabel();
			}
			return null;
		},

		/*
			 * Sets the context
			 * @param {string} context - id of the column
			 */
		_setContext: function(itemId) {
			var oldItemId = this._getContext();
			if (oldItemId !== itemId) {
				this._ensureState().context = { itemId: itemId };
				this._onStateChange();
			}
		},

		/*
			 * @return whether the state indicates a rank column should be shown in the visualization
			 */
		_getAddRank: function() {
			return this._state && this._state.rank;
		},

		/*
			 * Set whether to include rank in the visualization
			 * @param {boolean} addRank
			 */
		_setAddRank: function(addRank) {
			var oldRank = this._getAddRank();
			if (oldRank !== addRank) {
				this._ensureState().rank = addRank;
				this._onStateChange();
			}
		},

		render: function() {
			var topcount = {
					id: 'topcount',
					label: stringResources.get('topCount')
				},
				toppercent = {
					id: 'toppercent',
					label: stringResources.get('topPercent')
				},
				bottomcount = {
					id: 'bottomcount',
					label: stringResources.get('bottomCount')
				},
				bottompercent = {
					id: 'bottompercent',
					label: stringResources.get('bottomPercent')
				};

			var operations = [];

			if (this._allowPercentOperations) {
				operations = [topcount, toppercent, bottomcount, bottompercent];
			} else {
				operations = [topcount, bottomcount];
			}

			var dialogHtml = dot.template(TopBottomDialogTemplate)({
				id: _.uniqueId('searchMeasures'),

				clear: {
					disabled: !this._state,
					label: stringResources.get('topbottomClearLabel')
				},

				numberOfResults: {
					label: this._getSelectionLabel(),
					value: this._getValue(),
					desc: stringResources.get('topbottomNumberOfResultsLimit', {
						lower: MIN_NUMBER,
						higher: MAX_NUMBER
					})
				},

				show: {
					label: stringResources.get('topbottomOptionLabel'),
					desc: stringResources.get('topbottomSelectOption'),
					selected: this._getType(),
					options: operations
				},

				measures: this._contextColumns && {
					label: stringResources.get('searchByLabel'),
					desc: stringResources.get('searchColumn'),
					ariaLabel: stringResources.get('searchColumnAriaLabel')
				},

				rank: this.allowRankColumn && {
					label: stringResources.get('topbottomRankColumnLabel')
				}
			});
			this.$el
				.addClass('popoverTBDialogContainer topbottomDialog')
				.height(this._iHeight + 'px')
				.width(this._iWidth + 'px')
				.html(dialogHtml);

			this._populateContent();

			return this;
		},

		setFocus: function() {
			this.$valueInput.focus();
		},

		/*
			 * Verifies if the current Top-N state is valid, and thus can be applied to the visualization.
			 * Essentially, if there is no state, then it is valid; or if the state is complete, it is valid.
			 * Partially defined state are invalid.
			 * @return {boolean}
			 */
		_verifyState: function() {
			//Case where there the dialog's state is 'cleared'
			if (!this._isTopNOn()) {
				return true;
			}

			//If there is a context field, if it's not set, the dialog is not in a completed state
			if (this._contextColumns && !this._getContext()) {
				return false;
			}

			//type and value need to be set for the dialog to be in a completed state
			return !!(this._getType() && this._getValue());
		},

		/*
			 * sets focus for the search input box's parent div
			 * consistant with other input boxes across titan/helios
			 * when on focus the underline shows blue; on blur the underline shows grey
			 */
		_setFocusForInputBox: function() {
			this.$contextText.focus(function() {
				$(this).parent('div').addClass('focus-state');
			}).blur(function() {
				$(this).parent('div').removeClass('focus-state');
			});
		},

		/*
			 * Auto completes in search input
			 */
		_autoCompleteSearchBox: function() {
			this.$contextText.autocomplete({
				// reference: https://api.jqueryui.com/autocomplete/ -  custom source callback
				source: (request, response) => {
					const results = this._onSearch ? this._onSearch(request.term) : [];
					response( _.map(results, (column) => { return { columnId: column.getId(), value: column.getLabel() }; }));
				},
				appendTo: this.$el,
				position: {
					my: 'left top',
					at: 'left bottom',
					collision: 'flip'
				},
				select: function(event, ui) {
					//ui.item contains the select object
					this._setContext(ui.item.columnId);
				}.bind(this),
				response: function(event, ui) {
					//ui.content is an array that holds all of the searched results
					//if its length is 0, we set label and value to show a dropdown to indicate no matches found
					if (ui.content && !ui.content.length) {
						ui.content.push({
							label: stringResources.get('searchNoMatches'),
							value: stringResources.get('searchNoMatches')
						});
					}
				}
			}).data('ui-autocomplete')._renderItem = function(ul, item) {
				//no matches found case. set the class to disable from choosing/clicking by the user
				if (item.value === stringResources.get('searchNoMatches')) {
					return $('<li class="ui-state-disabled">' + item.label + '</li>').appendTo(ul);
				} else {
					//more normal case then label is appended to the list
					return $('<li>')
						.append('<a>' + item.label + '</a>')
						.appendTo(ul);
				}
			};
		},

		_populateContent: function() {
			if (this.allowRankColumn) {
				this._addRankCheckbox();
			}

			this.$valueInput = this.$('input[name="topbottomValue"]');
			this.$typeInput = this.$('input[name="topbottomType"]');
			this.$contextText = this.$('.searchByColumn :text');
			this.$clearButton = this.$('button[name="clear"]');
			this.$selectionLabel = this.$('.selectionlabel')[0].firstChild;

			this.$valueInput.on('change', function() {
				var n = this._getValueFromUI();
				if (n === null) {
					this.$valueInput.val(this._getValue());
				} else {
					this._setValue(n);
				}
			}.bind(this));
			this.$typeInput.on('change', function() {
				this._setType(this._getTypeFromUI());
			}.bind(this));
			this.$contextText.on('primaryaction', this._autoCompleteSearchBox.bind(this));
			this.$clearButton.on('click', this._clear.bind(this));

			this._updateContextUI();
			this._setFocusForInputBox();
		},

		/*
			 * @return {int} the number currently in the value input field in the UI if it is valid, `null` otherwise
			 */
		_getValueFromUI: function() {
			var n = parseInt(this.$valueInput.val(), 10);

			if (isNaN(n) || n < MIN_NUMBER || n > MAX_NUMBER) {
				return null;
			} else {
				return n;
			}
		},

		/*
			 * @return {string} the type currently selected in the UI, or `null` if none is selected
			 */
		_getTypeFromUI: function() {
			var checked = this.$typeInput.filter(function() {
				return $(this).is(':checked');
			});
			return (checked.length && checked.val()) || null;
		},

		/*
			 * Returns the UI to its initial state (with UI elements either cleared or in their initial state)
			 */
		_clearUI: function() {
			this.$valueInput.val(this._getValue());
			this.$typeInput.each(function() {
				$(this).prop('checked', false);
			});
			this._updateContextUI();
			if (this.allowRankColumn) {
				this._updateRankUI();
			}
		},

		_addRankCheckbox: function() {
			if (this._oRankCheckBox) {
				this._oRankCheckBox.remove();
			}
			this._oRankCheckBox = new CheckBox({
				'id': 'adding_a_rank_column_to_grid',
				'el': this.$('.topbottomRank'),
				'name': 'rankCheckBox',
				'label': '',
				'ariaLabel': stringResources.get('topbottomRankColumnLabel'),
				'checked': this._getAddRank(),
				'onChange': function(propName, propValue) {
					if (propName === 'rankCheckBox') {
						this._setAddRank(propValue);
					}
				}.bind(this)
			});
			this._oRankCheckBox.doRender();
			if (!this._oRankCheckBox.checked && !this._actionTypeSupportRank()) {
				this._oRankCheckBox.disable();
			}
		},

		/*
			 * Updates the text in the search box input field
			 * - Shows the selected column from an existing top/bottom
			 * - Clears the input field when limit is not defined
			 */
		_updateContextUI: function() {
			if (this._contextColumns) {
				var label = this._getContextLabel() || '';
				this.$contextText.val(label);
			}
		},

		_actionTypeSupportRank: function() {
			var type = this._getType();
			return type && (type === 'topcount' || type === 'bottomcount');
		},

		/*
			 * Reset the rank checkbox to initial state after clear,
			 * or update the check box based on what type of action is selected from the UI.
			 * Rank is supported only for topcount or bottomcount so enable the checkbox only for these 2 types.
			 */
		_updateRankUI: function() {
			if (this.allowRankColumn) {
				if (!this._state) {
					this._oRankCheckBox.uncheck(false);
					this._oRankCheckBox.disable();
					return;
				}

				if (this.allowRankColumn) {
					// enable rank only when the action type is either topcount or bottomcount.
					if (this._actionTypeSupportRank()) {
						this._oRankCheckBox.enable();
					} else {
						this._setAddRank(false);
						this._oRankCheckBox.uncheck(false);
						this._oRankCheckBox.disable();
					}
				}
			}
		}

	});
	return TopBottomView;
});
